import { t } from '@lingui/macro'
import { useField } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import NL from 'i18n-iso-countries/langs/nl.json'
import { isEmpty, isNil } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import { useDebounce } from 'usehooks-ts'

import { useCountryCodes } from '@/@bothrs-ui/web/hooks'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { useAzureMaps } from '@/modules/shared/hooks/useAzureMaps'
import { FlexColumn, FlexRow, Margin } from '../../layout'
import { Span } from '../../typography/Body'
import { Label } from '../label'
import { StSelectContainer } from '../select/Select.styled'
import {
  StContainer,
  StFieldContainer,
  StInputContainer,
  StInputErrorContainer,
  StInputIcon,
  StSuggestion,
  StSuggestionContainer,
} from './AddressAutocomplete.styled'

import type { AxiosResponse } from 'axios'
import type { ChangeEvent } from 'react'

const AnimatedFieldErrorContainer = motion(StInputErrorContainer)
const AnimatedIcon = motion(StInputIcon)

type AutocompleteAddress = {
  streetAddress: string
  city: string
  zipOrPostalCode: string
  country: string
}

type AddressAutocompleteProps = {
  id: string
  label?: string
  initialValue?: AutocompleteAddress
  placeholder: string
  required?: boolean
  ignoreTouched?: boolean
  setPostalCodeAndCityMismatch: React.Dispatch<React.SetStateAction<boolean>>
}

type AzureAddress = {
  streetNumber: string
  streetName: string
  municipality: string
  countrySecondarySubdivision: string
  countryTertiarySubdivision: string
  countrySubdivision: string
  countrySubdivisionName: string
  countrySubdivisionCode: string
  postalCode: string
  countryCode: string
  country: string
  countryCodeISO3: string
  freeformAddress: string
  localName: string
}

type AddressLookupResult = {
  type: 'Point Address' | 'Street' | string
  id: string
  score: number
  matchConfidence: {
    score: number
  }
  address: AzureAddress
}

const AddressAutocomplete = ({
  id,
  label,
  placeholder,
  required,
  ignoreTouched,
  setPostalCodeAndCityMismatch,
}: AddressAutocompleteProps) => {
  const theme = useTheme()
  const countryCodes = useCountryCodes('nl', [NL])

  const [_, metaProps, fullAddressHelpers] = useField(`${id}`)
  const [addressField, addressMeta, addressHelpers] = useField(`${id}.streetAddress`)
  const [postalCodeField, postalCodeMeta, postalCodeHelpers] = useField(`${id}.zipOrPostalCode`)
  const [cityField, cityMeta, cityHelpers] = useField(`${id}.city`)
  const [countryField, , countryHelpers] = useField(`${id}.country`)
  const inputRef = useRef<HTMLInputElement>(null)

  const [streetAddress, setStreetAddress] = useState<string>('')

  const debouncedInputValue = useDebounce(streetAddress, 500)
  const [hasFocus, setHasFocus] = useState(false)
  const [postalCodeAndCityMismatchError, setPostalCodeAndCityMismatchError] = useState(false)
  const [addresses, setAddresses] = useState<{
    results: AddressLookupResult[]
  }>({ results: [] })

  const {
    searchAddress: { execute: searchAddress, value: searchValue, isLoading, error: searchError },
  } = useAzureMaps()

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    // Only update when we have actual results; useAxiosAsync briefly sets value
    // to undefined at the start of each request which would flash the dropdown.
    if (searchValue) {
      setAddresses(searchValue)
    }
  }, [searchValue])

  const normalizedCountrySet = useMemo(() => {
    const value = (countryField.value ?? '').toString().trim().toUpperCase()
    return /^[A-Z]{2}$/.test(value) ? value : undefined
  }, [countryField.value])

  useEffect(() => {
    // Prevent HTML <select> from defaulting to the first option (e.g. "Åland") when empty.
    if (!normalizedCountrySet) {
      countryHelpers.setValue('BE')
    }
  }, [normalizedCountrySet, countryHelpers])

  // Template variables
  // ------------------------------------------------------------------------- /
  const options = useMemo(() => {
    if (addresses) {
      return addresses?.results
        ?.filter((result) => result.type === 'Point Address' || result.type === 'Street')
        .slice(0, 5)
        .map((address) => {
          const value = address.address
          const streetFromFreeform = value.freeformAddress?.split(',')?.[0]?.trim()

          return {
            label: value.freeformAddress,
            value: {
              // Preserve bus/letter by taking the street part of the freeformAddress when present.
              streetAddress: streetFromFreeform || `${value.streetName ?? ''} ${value.streetNumber ?? ''}`.trim(),
              city: value.municipality,
              zipOrPostalCode: value.postalCode,
              country: value.countryCode,
            },
          }
        })
    }
    return []
  }, [addresses])

  const showSuggestions = useMemo(() => {
    return hasFocus && !isNil(options) && !isEmpty(options)
  }, [hasFocus, options])

  const animatedIconProps = useMemo(
    () => ({
      initial: { opacity: 0, x: 30 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  const animatedErrorMessageProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  const hasStreetError = useMemo(
    () => (ignoreTouched || addressMeta.touched) && !isNil(addressMeta.error),
    [ignoreTouched, addressMeta.touched, addressMeta.error],
  )

  const hasPostalCodeError = useMemo(
    () => (ignoreTouched || postalCodeMeta.touched) && !isNil(postalCodeMeta.error),
    [ignoreTouched, postalCodeMeta.touched, postalCodeMeta.error],
  )

  const hasCityError = useMemo(
    () => (ignoreTouched || cityMeta.touched) && !isNil(cityMeta.error),
    [ignoreTouched, cityMeta.touched, cityMeta.error],
  )

  const hasPostalCodeAndCityError = useMemo(() => postalCodeAndCityMismatchError, [postalCodeAndCityMismatchError])

  const validatePostalCodeAndCity = async () => {
    if (postalCodeField.value && cityField.value) {
      await searchAddress(`${postalCodeField.value},${cityField.value}`, normalizedCountrySet)?.then(
        (addresses: AxiosResponse | void) => {
          if (addresses?.data.results) {
            const valid = addresses.data.results.some((result: AddressLookupResult) => {
              return (
                result.address.postalCode === postalCodeField.value && result.address.municipality === cityField.value
              )
            })

            setPostalCodeAndCityMismatch?.(!valid)
            setPostalCodeAndCityMismatchError?.(!valid)
          }
        },
      )
    }
  }

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (debouncedInputValue) {
      searchAddress(debouncedInputValue, normalizedCountrySet)
    } else {
      setAddresses({ results: [] })
    }
  }, [debouncedInputValue])

  // Interaction functions
  // ------------------------------------------------------------------------- /
  const handleStreetAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setStreetAddress(inputValue)
    setHasFocus(true)
    addressHelpers.setValue(inputValue)
    if (inputValue === '') {
      fullAddressHelpers.setValue({
        streetAddress: '',
        zipOrPostalCode: '',
        city: '',
        country: 'BE',
      })
    }
  }

  const handleInputBlur = () => {
    // Check if the focus is still on the input (e.g., the click happened on an li)
    if (document.activeElement === inputRef.current) {
      return
    }

    addressHelpers.setTouched(true, true)
    // Your logic when input loses focus (e.g., hide the dropdown)
    setHasFocus(false)
  }

  const handleSuggestionClick = (_: string, value: AutocompleteAddress) => {
    setPostalCodeAndCityMismatchError?.(false)
    setPostalCodeAndCityMismatch?.(false)

    fullAddressHelpers.setValue({
      streetAddress: value.streetAddress,
      zipOrPostalCode: value.zipOrPostalCode,
      city: value.city,
      country: value.country,
    })
    addressHelpers.setTouched(true, true)
    setHasFocus(false)
  }

  return (
    <StContainer>
      <FlexColumn gap={theme.UI.Spacing.Space2}>
        <StFieldContainer>
          <Label htmlFor={id} color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
            {label} {required ? '*' : null}
            {isLoading ? <AnimatedLoader /> : null}
          </Label>
          <StInputContainer error={hasStreetError}>
            <input
              {...addressField}
              ref={inputRef}
              type='text'
              placeholder={placeholder}
              autoComplete='off'
              onChange={handleStreetAddressChange}
              onBlur={handleInputBlur}
            />
            <AnimatePresence mode='popLayout' initial={false}>
              {hasStreetError ? (
                <AnimatedIcon key='error-icon' icon={['far', 'triangle-exclamation']} {...animatedIconProps} />
              ) : null}
            </AnimatePresence>
          </StInputContainer>
        </StFieldContainer>

        {(searchError || showSuggestions) && streetAddress.trim() ? (
          <StSuggestionContainer>
            {isLoading ? (
              <StSuggestion>
                <Span type='BodyMediumRegular'>Laden...</Span>
              </StSuggestion>
            ) : null}
            {searchError ? (
              <StSuggestion>
                <Span type='BodyMediumBold' color={theme.Colors.Status.Error}>
                  {t({
                    id: 'globals.validation.addressLookupFailed',
                    message: 'Adres zoeken mislukt. Probeer opnieuw.',
                  })}
                </Span>
              </StSuggestion>
            ) : null}
            {showSuggestions
              ? options.map((option: { label: string; value: AutocompleteAddress }, index: number) => (
                  <StSuggestion
                    key={`${option.label}_${index}`}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      handleSuggestionClick(option.label, option.value)
                    }}
                  >
                    <Span type='BodyMediumRegular'>{option?.label}</Span>
                  </StSuggestion>
                ))
              : null}
          </StSuggestionContainer>
        ) : null}

        <AnimatePresence mode='popLayout'>
          {hasStreetError ? (
            <AnimatedFieldErrorContainer key={addressMeta.error} {...animatedErrorMessageProps}>
              <Margin mt={theme.UI.Spacing.Space2} />
              <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                {addressMeta.error}
              </Span>
            </AnimatedFieldErrorContainer>
          ) : null}
        </AnimatePresence>

        <FlexRow gap={theme.UI.Spacing.Space2}>
          <FlexColumn style={{ width: '100%' }}>
            <StFieldContainer>
              <Label htmlFor='postal-code' color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
                Postcode *
              </Label>
              <StInputContainer error={hasPostalCodeError || hasPostalCodeAndCityError}>
                <input
                  {...postalCodeField}
                  autoComplete='off'
                  onBlur={() => {
                    postalCodeHelpers.setTouched(true)
                    validatePostalCodeAndCity()
                  }}
                />
                <AnimatePresence mode='popLayout' initial={false}>
                  {hasPostalCodeError || hasPostalCodeAndCityError ? (
                    <AnimatedIcon key='error-icon' icon={['far', 'triangle-exclamation']} {...animatedIconProps} />
                  ) : null}
                </AnimatePresence>
              </StInputContainer>
            </StFieldContainer>
            <AnimatePresence mode='popLayout'>
              {hasPostalCodeError ? (
                <AnimatedFieldErrorContainer key={postalCodeMeta.error} {...animatedErrorMessageProps}>
                  <Margin mt={theme.UI.Spacing.Space2} />
                  <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                    {t({
                      id: 'globals.validation.required',
                    })}
                  </Span>
                </AnimatedFieldErrorContainer>
              ) : hasPostalCodeAndCityError ? (
                <AnimatedFieldErrorContainer key={addressMeta.error} {...animatedErrorMessageProps}>
                  <Margin mt={theme.UI.Spacing.Space2} />
                  <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                    {t({
                      id: 'globals.validation.postalCodeAndCityMismatch',
                    })}
                  </Span>
                </AnimatedFieldErrorContainer>
              ) : null}
            </AnimatePresence>
          </FlexColumn>
          <FlexColumn style={{ width: '100%' }}>
            <StFieldContainer>
              <Label htmlFor='city' color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
                Stad *
              </Label>
              <StInputContainer error={hasCityError || hasPostalCodeAndCityError}>
                <input
                  {...cityField}
                  autoComplete='off'
                  onBlur={() => {
                    cityHelpers.setTouched(true)
                    validatePostalCodeAndCity()
                  }}
                />
                <AnimatePresence mode='popLayout' initial={false}>
                  {hasCityError || hasPostalCodeAndCityError ? (
                    <AnimatedIcon key='error-icon' icon={['far', 'triangle-exclamation']} {...animatedIconProps} />
                  ) : null}
                </AnimatePresence>
              </StInputContainer>
            </StFieldContainer>
            <AnimatePresence mode='popLayout'>
              {hasCityError ? (
                <AnimatedFieldErrorContainer key={cityMeta.error} {...animatedErrorMessageProps}>
                  <Margin mt={theme.UI.Spacing.Space2} />
                  <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
                    {t({
                      id: 'globals.validation.required',
                    })}
                  </Span>
                </AnimatedFieldErrorContainer>
              ) : null}
            </AnimatePresence>
          </FlexColumn>
        </FlexRow>
        <StFieldContainer>
          <Label htmlFor='country' color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
            Land *
          </Label>
          <StSelectContainer {...countryField} autoComplete='off'>
            {countryCodes.map((countryCode) => (
              <option key={countryCode.value} value={countryCode.value}>
                {countryCode.label}
              </option>
            ))}
          </StSelectContainer>
        </StFieldContainer>
      </FlexColumn>
    </StContainer>
  )
}

export { AddressAutocomplete }
